/**
 * Grill Atlas — Field Edition Architectural Interaction & Motion Engine
 * Powered by Lenis Smooth Scroll & Emil Kowalski-tier Animation Craft.
 */

(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // ==========================================================================
  // 1. Lenis Smooth Scroll Initialization (Apple/Awwwards Luxury Feel)
  // ==========================================================================
  let lenis = null;
  const initLenis = () => {
    if (!reducedMotion.matches && typeof window.Lenis !== 'undefined' && !lenis) {
      lenis = new window.Lenis({
        duration: 1.15,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 0.95,
        touchMultiplier: 1.5,
      });

      function raf(time) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }
      requestAnimationFrame(raf);
      window.gaLenis = lenis;
    }
  };

  initLenis();
  window.addEventListener('load', initLenis);

  // ==========================================================================
  // ==========================================================================
  // 2. High-Performance Universal Intersection Observer for Scroll Reveals
  // ==========================================================================
  const setupReveals = () => {
    const revealElements = document.querySelectorAll(
      '.af-reveal, .ga-reveal, [data-af-reveal], .af-product-card, .af-fire-chapter, .ga-spec-card, .ga-review-card, .ga-section-heading'
    );

    if (!('IntersectionObserver' in window)) {
      revealElements.forEach((el) => {
        el.classList.add('is-in', 'ga-visible');
      });
      return;
    }

    const isMobile = window.innerWidth <= 780;
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in', 'ga-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: isMobile ? 0.01 : 0.06,
        rootMargin: isMobile ? '0px 0px -20px 0px' : '0px 0px -50px 0px',
      }
    );

    revealElements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('is-in', 'ga-visible');
      } else {
        revealObserver.observe(el);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupReveals);
  } else {
    setupReveals();
  }
  document.addEventListener('shopify:section:load', setupReveals);

  // ==========================================================================
  // 3. Hero Scroll-Driven Parallax & Scale Motion (Mobile + Desktop)
  // ==========================================================================
  const setupHeroScroll = () => {
    const hero = document.querySelector('.af-opening');
    if (!hero || reducedMotion.matches) return;

    const heroImg = document.querySelector('#af-hero-img');
    const heroCopy = document.querySelector('.af-opening-copy');
    const heroStamp = document.querySelector('[data-af-hero-stamp]');

    let ticking = false;
    const onHeroScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const heroH = hero.offsetHeight;

      if (scrollY <= heroH + 120) {
        const progress = Math.min(1, Math.max(0, scrollY / heroH));
        const isMobile = window.innerWidth <= 780;

        if (heroImg) {
          const scale = 1.06 - progress * 0.08;
          const translateY = progress * (isMobile ? 24 : 50);
          heroImg.style.transform = `scale(${scale}) translateY(${translateY}px)`;
        }

        if (heroCopy && isMobile) {
          const translateY = progress * 20;
          const opacity = Math.max(0, 1 - progress * 1.25);
          heroCopy.style.transform = `translateY(-${translateY}px)`;
          heroCopy.style.opacity = opacity;
        }

        if (heroStamp) {
          const stampY = progress * (isMobile ? -10 : -25);
          heroStamp.style.transform = `translateY(${stampY}px)`;
        }
      }
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(onHeroScroll);
          ticking = true;
        }
      },
      { passive: true }
    );

    onHeroScroll();
  };
  setupHeroScroll();

  // ==========================================================================
  // 4. Header Scroll Elevation & Sticky Actions Bar
  // ==========================================================================
  const header = document.querySelector('.ga-header');
  const stickyBar = document.querySelector('.ga-sticky-bar');
  const buyHero = document.querySelector('.ga-product-hero') || document.querySelector('.af-pdp');

  let lastScrollY = 0;
  let headerTicking = false;

  const onHeaderScroll = () => {
    const scrollY = window.scrollY || window.pageYOffset;

    if (header) {
      header.classList.toggle('ga-scrolled', scrollY > 20);
    }

    if (stickyBar && buyHero) {
      const heroBottom = buyHero.offsetTop + buyHero.offsetHeight - 200;
      stickyBar.classList.toggle('is-visible', scrollY > heroBottom);
    }

    lastScrollY = scrollY;
    headerTicking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (!headerTicking) {
        requestAnimationFrame(onHeaderScroll);
        headerTicking = true;
      }
    },
    { passive: true }
  );

  // ==========================================================================
  // 5. Fire Chapter Scroll Pinning & Mobile Interactive Story (Home Narrative)
  // ==========================================================================
  const setupFireStory = () => {
    const stories = document.querySelectorAll('[data-af-story]');
    if (!stories.length) return;

    stories.forEach((story) => {
      const stage = story.querySelector('.af-fire-stage');
      const chapters = [...story.querySelectorAll('[data-af-chapter]')];
      if (!stage || !chapters.length) return;

      const jumpBtns = story.querySelectorAll('[data-af-jump]');

      // Jump button click handler (smooth mobile & desktop scrolling)
      jumpBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const targetIndex = Number(btn.dataset.afJump) || 0;
          const isMobile = window.innerWidth <= 780;

          if (isMobile) {
            const targetEl = document.getElementById(`af-chapter-${targetIndex}`);
            if (targetEl) {
              const targetY = targetEl.getBoundingClientRect().top + window.scrollY - 80;
              window.scrollTo({ top: targetY, behavior: 'smooth' });
            }
            jumpBtns.forEach((b) => {
              const matches = Number(b.dataset.afJump) === targetIndex;
              b.classList.toggle('is-active', matches);
              b.setAttribute('aria-pressed', String(matches));
            });
          } else {
            const offset = parseFloat(getComputedStyle(stage).top) || 0;
            const scrollDistance = story.offsetHeight - stage.offsetHeight;
            const targetY = story.offsetTop - offset + (scrollDistance * (targetIndex + 0.1)) / chapters.length;

            if (lenis) {
              lenis.scrollTo(targetY);
            } else {
              window.scrollTo({ top: targetY, behavior: 'smooth' });
            }
          }
        });
      });

      if (reducedMotion.matches) {
        chapters.forEach((el) => (el.inert = false));
        return;
      }

      const updateFire = () => {
        const isMobile = window.innerWidth <= 780;
        if (isMobile) {
          const viewportMid = window.innerHeight * 0.45;
          let activeIndex = 0;
          chapters.forEach((ch, idx) => {
            const rect = ch.getBoundingClientRect();
            if (rect.top <= viewportMid && rect.bottom >= viewportMid) {
              activeIndex = idx;
            }
          });
          jumpBtns.forEach((b) => {
            const idx = Number(b.dataset.afJump);
            b.classList.toggle('is-active', idx === activeIndex);
            b.setAttribute('aria-pressed', String(idx === activeIndex));
          });
          return;
        }

        const offset = parseFloat(getComputedStyle(stage).top) || 0;
        const rect = story.getBoundingClientRect();
        const scrollDistance = Math.max(1, story.offsetHeight - stage.offsetHeight);
        const progress = Math.max(0, Math.min(1, (offset - rect.top) / scrollDistance));
        const beat = Math.min(chapters.length - 1, Math.floor(progress * chapters.length));

        stage.style.setProperty('--af-progress', progress);

        chapters.forEach((el, index) => {
          const active = index === beat;
          el.classList.toggle('is-active', active);
          el.inert = !active;
          el.style.setProperty('--af-local', Math.max(0, Math.min(1, progress * chapters.length - index)));
        });

        jumpBtns.forEach((btn, index) => {
          btn.setAttribute('aria-pressed', String(index === beat));
        });
      };

      window.addEventListener('scroll', updateFire, { passive: true });
      updateFire();
    });
  };
  setupFireStory();

  // ==========================================================================
  // 5. Product Detail Page (PDP) Media Gallery & Variant Engine
  // ==========================================================================
  const setupPDP = () => {
    // Gallery Thumbs
    const stageImg = document.getElementById('ga-stage-img');
    const thumbBtns = document.querySelectorAll('.ga-thumb-btn');

    thumbBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const fullSrc = btn.getAttribute('data-full-src');
        const altText = btn.getAttribute('data-alt');

        if (stageImg && fullSrc) {
          stageImg.style.opacity = '0.3';
          stageImg.src = fullSrc;
          if (altText) stageImg.alt = altText;
          setTimeout(() => {
            stageImg.style.opacity = '1';
          }, 120);
        }

        thumbBtns.forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
      });
    });

    // Variant Selection Pills
    const variantPills = document.querySelectorAll('.ga-pill-btn');
    const variantSelect = document.getElementById('ga-variant-select');
    const priceDisplay = document.getElementById('ga-price-display');
    const compareDisplay = document.getElementById('ga-compare-display');
    const mainFormInput = document.getElementById('ga-selected-variant-id');
    const stickyPrice = document.querySelector('.ga-sticky-price');
    const addToBagBtn = document.getElementById('ga-add-to-bag-btn');

    variantPills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const variantId = pill.getAttribute('data-variant-id');
        const price = pill.getAttribute('data-price');
        const compareAt = pill.getAttribute('data-compare-at');
        const available = pill.getAttribute('data-available') === 'true';

        // Update pills active state
        variantPills.forEach((p) => p.classList.remove('is-active'));
        pill.classList.add('is-active');

        // Update hidden form inputs
        if (mainFormInput) mainFormInput.value = variantId;
        if (variantSelect) variantSelect.value = variantId;

        // Update price display
        if (priceDisplay && price) priceDisplay.textContent = price;
        if (stickyPrice && price) stickyPrice.textContent = price;
        if (compareDisplay) {
          if (compareAt) {
            compareDisplay.textContent = compareAt;
            compareDisplay.style.display = 'inline';
          } else {
            compareDisplay.style.display = 'none';
          }
        }

        // Update CTA state
        if (addToBagBtn) {
          if (available) {
            addToBagBtn.disabled = false;
            addToBagBtn.innerHTML = `ADD TO BAG ↗ <span style="opacity:0.6;margin:0 8px;">·</span> ${price}`;
          } else {
            addToBagBtn.disabled = true;
            addToBagBtn.textContent = 'SOLD OUT';
          }
        }
      });
    });

    // Quantity Stepper
    const qtyInput = document.getElementById('ga-quantity-input');
    const minusBtn = document.getElementById('ga-qty-minus');
    const plusBtn = document.getElementById('ga-qty-plus');

    if (qtyInput && minusBtn && plusBtn) {
      minusBtn.addEventListener('click', () => {
        const val = Math.max(1, parseInt(qtyInput.value || '1', 10) - 1);
        qtyInput.value = val;
      });
      plusBtn.addEventListener('click', () => {
        const val = Math.min(10, parseInt(qtyInput.value || '1', 10) + 1);
        qtyInput.value = val;
      });
    }

    // Ajax Add-To-Cart Form Handler
    const pdpForm = document.getElementById('ga-product-form');
    if (pdpForm) {
      pdpForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const submitBtn = pdpForm.querySelector('.ga-primary-btn');
        const origText = submitBtn ? submitBtn.innerHTML : 'ADD TO BAG ↗';

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'ADDING TO BAG...';
        }

        try {
          const formData = new FormData(pdpForm);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });

          if (!res.ok) {
            const err = await res.json();
            throw new Error(err.description || 'Unable to add to bag');
          }

          if (submitBtn) {
            submitBtn.textContent = 'ADDED TO BAG ✓';
            setTimeout(() => {
              submitBtn.disabled = false;
              submitBtn.innerHTML = origText;
            }, 2000);
          }

          // Update header bag count
          const cartRes = await fetch('/cart.js');
          if (cartRes.ok) {
            const cartData = await cartRes.json();
            document.querySelectorAll('.ga-bag-count').forEach((badge) => {
              badge.textContent = cartData.item_count;
            });
          }
        } catch (err) {
          alert(err.message || 'Could not add item to bag.');
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = origText;
          }
        }
      });
    }

    // Sticky Buy Bar trigger
    const stickyBtn = document.querySelector('.ga-sticky-btn');
    if (stickyBtn && pdpForm) {
      stickyBtn.addEventListener('click', () => {
        pdpForm.requestSubmit();
      });
    }
  };
  setupPDP();

  // ==========================================================================
  // 6. Interactive Simulator Workbench
  // ==========================================================================
  const setupSimulator = () => {
    const simRoot = document.getElementById('atlas-interactive-simulator');
    if (!simRoot) return;

    const modePills = simRoot.querySelectorAll('.ga-mode-pill');
    const capacitySlider = document.getElementById('ga-capacity-slider');
    const sliderBadge = document.getElementById('ga-slider-badge');
    const tempDisplay = document.getElementById('ga-gauge-temp');
    const celsiusDisplay = document.getElementById('ga-gauge-celsius');
    const gaugeFill = document.getElementById('ga-gauge-fill');
    const rampDisplay = document.getElementById('ga-readout-ramp');
    const airDisplay = document.getElementById('ga-readout-air');
    const retentionDisplay = document.getElementById('ga-readout-retention');
    const metricThroughput = document.getElementById('ga-metric-throughput');
    const metricConsumption = document.getElementById('ga-metric-consumption');
    const metricRecovery = document.getElementById('ga-metric-recovery');
    const protocolDesc = document.getElementById('ga-protocol-desc');

    const modes = [
      {
        id: 'sear',
        tempF: 850,
        tempC: 454,
        offset: 40,
        ramp: '11 mins to peak',
        air: 'Direct Infrared High',
        retention: '98.4% Refractory',
        throughput: '34 Patties / 6 Steaks',
        consumption: '1.2 lbs LP / hr',
        recovery: '< 8 Seconds',
        protocol: 'Prime Bone-In Ribeye or Tomahawk. Sear 90 seconds per side over the infrared zone, rest for 8 minutes at indirect perimeter.',
      },
      {
        id: 'roast',
        tempF: 425,
        tempC: 218,
        offset: 120,
        ramp: '7 mins to temp',
        air: 'Convection Balanced 50%',
        retention: '99.1% Stable',
        throughput: '2 Whole Spatchcock Birds',
        consumption: '0.6 lbs LP / hr',
        recovery: '< 12 Seconds',
        protocol: 'Whole Roaster Chicken or Porchetta. Close hood vents to 30% for uniform rotary hot-air vortex cooking without burning.',
      },
      {
        id: 'smoke',
        tempF: 225,
        tempC: 107,
        offset: 185,
        ramp: '4 mins to stabilization',
        air: 'Low Ribbon Clean Draft',
        retention: '99.7% Airtight',
        throughput: '3 Full St. Louis Rib Racks',
        consumption: '0.3 lbs LP / hr',
        recovery: '< 15 Seconds',
        protocol: 'USDA Prime Brisket or Pork Butt. Add post-oak chunks directly over the heat diffusers. Sustained thin blue smoke for 12+ hours.',
      },
    ];

    let currentMode = modes[0];

    const updateSim = () => {
      if (tempDisplay) tempDisplay.textContent = `${currentMode.tempF}°F`;
      if (celsiusDisplay) celsiusDisplay.textContent = `${currentMode.tempC}°C`;
      if (rampDisplay) rampDisplay.textContent = currentMode.ramp;
      if (airDisplay) airDisplay.textContent = currentMode.air;
      if (retentionDisplay) retentionDisplay.textContent = currentMode.retention;
      if (protocolDesc) protocolDesc.textContent = currentMode.protocol;

      if (gaugeFill) {
        gaugeFill.style.strokeDashoffset = currentMode.offset;
      }

      // Guest slider multiplier
      const guestSteps = ['4 Guests (Intimate Cook)', '8 Guests (Family Table)', '16 Guests (Weekend Feast)', '24+ Guests (Catering Run)'];
      const val = parseInt(capacitySlider?.value || '2', 10);
      if (sliderBadge) sliderBadge.textContent = guestSteps[val - 1] || '12 Guests';

      const factor = val;
      if (metricThroughput) metricThroughput.textContent = `${factor * 8} Portions / Turn`;
      if (metricConsumption) metricConsumption.textContent = `${(0.4 * factor).toFixed(1)} lbs Fuel / hr`;
      if (metricRecovery) metricRecovery.textContent = `< ${6 + factor * 2} Seconds`;
    };

    modePills.forEach((pill, idx) => {
      pill.addEventListener('click', () => {
        modePills.forEach((p) => p.classList.remove('ga-active'));
        pill.classList.add('ga-active');
        currentMode = modes[idx] || modes[0];
        updateSim();
      });
    });

    if (capacitySlider) {
      capacitySlider.addEventListener('input', updateSim);
    }

    updateSim();
  };
  setupSimulator();

  // ========================================================================
  // ==========================================================================
  // 7. Native Dialogs & Grill Finder Quiz Engine (Rock Solid)
  // ==========================================================================
  let previousFocus;
  const openDialog = (dialog) => {
    if (!dialog) return;
    document.querySelectorAll('.ga-native-dialog[open], dialog[open]').forEach((el) => {
      closeDialog(el);
    });
    previousFocus = document.activeElement;
    dialog.style.setProperty('display', 'flex', 'important');
    dialog.removeAttribute('aria-hidden');
    try {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    } catch(e) {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('ga-dialog-open');
  };

  const closeDialog = (dialog) => {
    if (!dialog) return;
    try {
      if (typeof dialog.close === 'function') {
        dialog.close();
      }
    } catch(e) {}
    dialog.removeAttribute('open');
    dialog.setAttribute('aria-hidden', 'true');
    dialog.style.setProperty('display', 'none', 'important');
    document.documentElement.classList.remove('ga-dialog-open');
    if (previousFocus && typeof previousFocus.focus === 'function') {
      try { previousFocus.focus(); } catch(e) {}
    }
  };

  const resetFinder = () => {
    const form = document.getElementById('ga-finder-form');
    if (form) {
      form.reset();
      form.hidden = false;
    }
    const result = document.getElementById('ga-finder-result');
    if (result) result.hidden = true;
  };

  // Close buttons delegation
  document.addEventListener('click', (event) => {
    const closeBtn = event.target.closest('[data-ga-close], .ga-dialog-close');
    if (closeBtn) {
      event.preventDefault();
      event.stopPropagation();
      const dlg = closeBtn.closest('dialog') || document.getElementById('ga-finder') || document.getElementById('ga-mobile-nav');
      closeDialog(dlg);
      return;
    }

    const el = event.target.closest('button, a');
    if (!el) return;

    if (el.matches('[data-ga-finder]')) {
      event.preventDefault();
      resetFinder();
      openDialog(document.getElementById('ga-finder'));
    }

    if (el.matches('[data-ga-menu], .ga-mobile-menu')) {
      event.preventDefault();
      openDialog(document.getElementById('ga-mobile-nav'));
    }

    if (el.matches('[data-ga-restart]')) {
      event.preventDefault();
      resetFinder();
      const firstInput = document.querySelector('#ga-finder-form input');
      if (firstInput) firstInput.focus();
    }

    if (el.matches('#ga-mobile-nav a')) {
      closeDialog(el.closest('dialog'));
    }

    if (el.matches('[data-ga-cart]')) {
      const drawer = document.getElementById('cart-drawer') || document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open();
      }
    }

    if (el.matches('[data-ga-search]')) {
      const search = document.getElementById('search-modal') || document.querySelector('search-modal');
      if (search && typeof search.showDialog === 'function') {
        event.preventDefault();
        search.showDialog();
      }
    }
  });

  // Close on backdrop click
  document.addEventListener('click', (event) => {
    if (event.target.tagName === 'DIALOG' && event.target.hasAttribute('open')) {
      const r = event.target.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) {
        closeDialog(event.target);
      }
    }
  });

  // Close on Escape
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      document.querySelectorAll('.ga-native-dialog[open], dialog[open]').forEach((d) => closeDialog(d));
    }
  });

  // Native close event listener
  document.addEventListener('close', (event) => {
    if (event.target.matches('.ga-native-dialog, dialog')) {
      closeDialog(event.target);
    }
  }, true);

  // Initial load safety: close and hide all native dialogs
  const initDialogs = () => {
    document.querySelectorAll('.ga-native-dialog, #ga-finder, #ga-mobile-nav').forEach((d) => {
      try { if (d.open) d.close(); } catch(e) {}
      d.removeAttribute('open');
      d.setAttribute('aria-hidden', 'true');
      d.style.setProperty('display', 'none', 'important');
    });
    document.documentElement.classList.remove('ga-dialog-open');
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDialogs);
  } else {
    initDialogs();
  }

  // Grill Finder Quiz Handling
  document.addEventListener('submit', (event) => {
    if (event.target.id !== 'ga-finder-form') return;
    event.preventDefault();
    const data = new FormData(event.target);
    const fuel = data.get('fuel') || 'gas';
    const finder = document.getElementById('ga-finder');
    if (!finder) return;

    const labels = {
      gas: 'Gas is your kind of fire.',
      charcoal: 'Take the charcoal trail.',
      pizza: 'Pizza night is calling.'
    };

    const heading = finder.querySelector('[data-ga-result-heading]');
    if (heading) heading.textContent = labels[fuel] || labels.gas;

    const copy = finder.querySelector('[data-ga-result-copy]');
    if (copy) {
      copy.textContent = data.get('people') === 'crowd'
        ? 'Look for generous cooking space and dual-zone heat to keep hosting relaxed.'
        : 'Start with a compact setup that gives you precision control without crowding your patio.';
    }

    const link = finder.querySelector('[data-ga-result-link]');
    if (link) {
      const matchUrl = finder.getAttribute(`data-${fuel}-url`) || '/collections/all';
      link.href = matchUrl;
    }

    event.target.hidden = true;
    const result = document.getElementById('ga-finder-result');
    if (result) {
      result.hidden = false;
      result.focus();
    }
  });

})();
